<script setup lang="ts">
import { computed } from 'vue';
import Chart from 'primevue/chart';
import type { PRData } from '@/backend/metrics.backend';
import { DateTime } from 'luxon';
import Skeleton from 'primevue/skeleton';
import Button from 'primevue/button';

interface Props {
  prData: PRData[];
  loading: boolean;
  error: string | null;
  aggregationMode?: 'pr-count' | 'total-loc';
}

const props = withDefaults(defineProps<Props>(), {
  aggregationMode: 'pr-count'
});

const emit = defineEmits<{
  'update:aggregationMode': [mode: 'pr-count' | 'total-loc'];
  retry: [];
}>();

// Format AI contribution labels
function formatAiContributionLabel(aiContribution: string | null | undefined): string {
  if (!aiContribution || aiContribution === 'none') {
    return 'N/A';
  }
  
  const labels: Record<string, string> = {
    'research': 'Research Only',
    'minor': 'Minor',
    'moderate': 'Moderate',
    'major': 'Major',
    'most': 'Most',
    'primary': 'Primary',
    'tests-only': 'Tests Only',
    'review': 'Review Only',
    'other': 'Other'
  };
  
  return labels[aiContribution] || aiContribution;
}

// Color palette for AI contribution categories
const AI_CONTRIBUTION_COLORS: Record<string, string> = {
  'n/a': '#94A3B8',           // gray for N/A
  'research': '#E0E7FF',      // very light indigo
  'minor': '#BFDBFE',         // light blue
  'moderate': '#93C5FD',      // blue
  'major': '#60A5FA',         // darker blue
  'most': '#3B82F6',          // strong blue
  'primary': '#2563EB',       // deep blue
  'tests-only': '#A78BFA',    // purple
  'review': '#C084FC',        // lighter purple
  'other': '#D4D4D8'          // neutral gray
};

// Process data and calculate aggregations
const chartData = computed(() => {
  if (props.prData.length === 0) {
    return null;
  }

  // Group by AI contribution category
  const grouped = new Map<string, { count: number; totalLoc: number }>();

  props.prData.forEach(pr => {
    // Normalize the key - treat null, undefined, or 'none' as 'N/A'
    const rawCategory = pr.aiContribution;
    const category = (!rawCategory || rawCategory === 'none') ? 'n/a' : rawCategory.toLowerCase();
    
    if (!grouped.has(category)) {
      grouped.set(category, { count: 0, totalLoc: 0 });
    }
    
    const group = grouped.get(category)!;
    group.count += 1;
    group.totalLoc += pr.totalLoc;
  });

  // Sort by the selected aggregation metric
  const sortedEntries = Array.from(grouped.entries()).sort((a, b) => {
    const metricA = props.aggregationMode === 'pr-count' ? a[1].count : a[1].totalLoc;
    const metricB = props.aggregationMode === 'pr-count' ? b[1].count : b[1].totalLoc;
    return metricB - metricA;
  });

  // Prepare chart data
  const labels: string[] = [];
  const data: number[] = [];
  const backgroundColor: string[] = [];

  sortedEntries.forEach(([category, stats]) => {
    const label = formatAiContributionLabel(category === 'n/a' ? null : category);
    labels.push(label);
    
    const value = props.aggregationMode === 'pr-count' ? stats.count : stats.totalLoc;
    data.push(value);
    
    const color = AI_CONTRIBUTION_COLORS[category] || '#9CA3AF';
    backgroundColor.push(color);
  });

  return {
    labels,
    datasets: [{
      data,
      backgroundColor,
      borderColor: backgroundColor.map(c => c),
      borderWidth: 1
    }]
  };
});

// Chart options
const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  devicePixelRatio: 1,
  plugins: {
    legend: {
      position: 'right' as const,
      align: 'center' as const,
      labels: {
        usePointStyle: true,
        padding: 12,
        boxWidth: 8,
        font: {
          size: 10
        }
      }
    },
    tooltip: {
      callbacks: {
        label: (context: any) => {
          const label = context.label || '';
          const value = context.parsed || 0;
          const dataset = context.dataset;
          const total = dataset.data.reduce((sum: number, val: number) => sum + val, 0);
          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
          
          if (props.aggregationMode === 'pr-count') {
            return `${label}: ${value} PR${value !== 1 ? 's' : ''} (${percentage}%)`;
          } else {
            return `${label}: ${value.toLocaleString()} LOC (${percentage}%)`;
          }
        }
      }
    }
  },
  layout: {
    padding: {
      left: 0,
      right: 0,
      top: 0,
      bottom: 0
    }
  },
  elements: {
    arc: {
      borderWidth: 1
    }
  }
}));

// Total stats for accessibility
const totalStats = computed(() => {
  if (!chartData.value) return null;
  
  const total = chartData.value.datasets[0].data.reduce((sum: number, val: number) => sum + val, 0);
  const categories = chartData.value.labels.length;
  
  return {
    total,
    categories,
    metric: props.aggregationMode === 'pr-count' ? 'PRs' : 'LOC'
  };
});

// Retry function
function retry() {
  emit('retry');
}

</script>

<template>
  <div class="metrics-ai-contribution-chart">
    <!-- Loading State -->
    <div v-if="loading" class="space-y-4">
      <Skeleton height="200px" class="mb-4" />
      <div class="flex justify-center gap-4">
        <Skeleton width="80px" height="20px" />
        <Skeleton width="120px" height="20px" />
        <Skeleton width="100px" height="20px" />
      </div>
    </div>
    
    <!-- Error State -->
    <div v-else-if="error" class="text-center py-8">
      <div class="text-red-600 mb-4">
        <i class="pi pi-exclamation-triangle text-2xl mb-2"></i>
        <p class="text-sm">{{ error }}</p>
      </div>
      <Button 
        label="Retry" 
        icon="pi pi-refresh" 
        @click="retry"
        severity="secondary"
      />
    </div>
    
    <!-- No Data State -->
    <div v-else-if="!chartData" class="text-center py-16">
      <div class="text-muted-color mb-4">
        <i class="pi pi-chart-pie text-4xl mb-4 block"></i>
        <p class="text-lg font-medium mb-2">No PR Data</p>
        <p class="text-sm">No pull request data available for this period.</p>
      </div>
    </div>
    
    <!-- Chart -->
    <div v-else class="chart-wrapper">
      <!-- Pie Chart -->
      <div class="chart-container">
        <Chart
          type="pie"
          class="pie-chart"
          :data="chartData"
          :options="chartOptions"
          :key="`ai-contrib-chart-${chartData?.labels?.length || 0}-${aggregationMode}`"
          :canvasProps="{
            'role': 'img',
            'aria-label': 'AI contribution breakdown pie chart'
          }"
        />
      </div>
      
      <!-- Accessibility Summary -->
      <div v-if="totalStats" class="sr-only">
        <p>AI contribution breakdown:</p>
        <ul>
          <li v-for="(label, index) in chartData!.labels" :key="label">
            {{ label }}: {{ chartData!.datasets[0].data[index] }} {{ totalStats.metric }}
          </li>
        </ul>
        <p>
          Total of {{ totalStats.categories }} categories representing {{ totalStats.total }} {{ totalStats.metric }}.
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.metrics-ai-contribution-chart {
  width: 100%;
}

.chart-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem 0;
}

.chart-container {
  width: 100%;
  max-width: 800px;
  height: 280px;
  position: relative;
}

.pie-chart {
  width: 100% !important;
  height: 100% !important;
}

/* Responsive adjustments */
@media (max-width: 1280px) {
  .chart-container {
    max-width: 100%;
    height: 300px;
  }
}

@media (max-width: 768px) {
  .chart-container {
    height: 280px;
  }
}
</style>
